/**
 * Achievements, titles and ornaments. Bot sources: `rpg/success/checks.ts`
 * (one check per group), `rpg/success/award.ts` (`updateSuccess`: rewards +
 * cascading success-points check), `rpg/titles.ts`, `rpg/ornements.ts`.
 *
 * Until T4.11 applies the shared fix P-18 to both clients, the web checks a
 * group exactly where the bot does (T3.3): level and missions when a mission
 * is collected, intro when a mission starts, money and divers when the
 * profile is opened, success points after every award. The other groups
 * (boss, toutous, damage, bet, chafer, guild, legacy gacha) are never checked
 * by the bot on the systems the web plays, so the web does not award them.
 */
import { successDefs, type SuccessDef } from '../data/index.js';
import { ornaments } from '../data/ornaments.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import type { BotUser } from '../state.js';

/** Groups the web checks today (their bot call sites exist on the web). */
export type SuccessGroup = 'level' | 'success' | 'missions' | 'intro' | 'money' | 'divers';
export const CHECKED_GROUPS: readonly SuccessGroup[] = ['level', 'success', 'missions', 'intro', 'money', 'divers'];

export interface SuccessProgress {
  current: number;
  target: number;
  /** Calendar achievements (`rewardEvent`): unlocked on that date when the profile is opened. */
  event?: 'christmas' | 'halloween' | 'stvalentin';
}

/** Month (0-based) and day of the calendar achievements (`checkDiversSuccess`). */
export const SUCCESS_EVENTS = {
  stvalentin: { month: 1, day: 14 },
  halloween: { month: 9, day: 31 },
  christmas: { month: 11, day: 25 },
} as const;

function valueOf(def: SuccessDef): { type: string; data: number; raw: unknown } | null {
  const v = def.value;
  if (typeof v === 'number') return { type: 'number', data: v, raw: v };
  if (typeof v !== 'object' || v === null) return null;
  return { type: String(v.type), data: typeof v.data === 'number' ? v.data : Number(v.data), raw: v.data };
}

function isGroupChecked(group: string): group is SuccessGroup {
  return (CHECKED_GROUPS as readonly string[]).includes(group);
}

/** The counter an achievement reads (`checks.ts`), or null when the web never checks it. */
function counter(save: BotUser, def: SuccessDef, v: { type: string; data: number }): number | null {
  const s = save.stats;
  switch (def.group) {
    case 'level':
      return save.level.level;
    case 'success':
      return save.success.points;
    case 'missions':
      return v.type === 'count' ? s.mission.count : v.type === 'fake' ? s.mission.fake : v.type === 'loot' ? s.mission.loot : v.type === 'xp' ? s.mission.xp : null;
    case 'money':
      return v.type === 'total' ? s.money.total : v.type === 'inventory' ? save.money : v.type === 'boss' ? s.money.boss : null;
    case 'intro':
      switch (v.type) {
        case 'boss': return s.boss.count;
        case 'mission': return s.mission.count;
        case 'bet': return save.bet.nb;
        case 'toutou': return s.dofus.time;
        case 'roll-1': return s.roll.count1;
        case 'roll-2': return s.roll.count2;
        default: return null;
      }
    case 'divers':
      if (v.type === 'dailyReward') return s.reward.inARow;
      if (v.type === 'reward') return s.reward.count;
      if (v.type === 'minibossSpawn') return save.miniboss ? save.miniboss.classic + save.miniboss.golden + save.miniboss.diamond : 0;
      return null;
    default:
      return null;
  }
}

/** `userHasAllIntro`: every intro achievement but "done" is unlocked. */
function hasAllIntro(save: BotUser): boolean {
  return successDefs
    .filter((d) => d.group === 'intro' && valueOf(d)?.type !== 'done')
    .every((d) => save.success.unlocked.includes(d.id));
}

/** Progress towards an achievement, or null when the web never awards it (see header). */
export function successProgress(save: BotUser, def: SuccessDef): SuccessProgress | null {
  if (!isGroupChecked(def.group)) return null;
  const v = valueOf(def);
  if (!v) return null;
  const done = save.success.unlocked.includes(def.id);
  if (def.group === 'divers' && v.type === 'rewardEvent') {
    const event = String(v.raw) as keyof typeof SUCCESS_EVENTS;
    if (!(event in SUCCESS_EVENTS)) return null;
    return { current: done ? 1 : 0, target: 1, event };
  }
  if (def.group === 'intro' && v.type === 'done') return { current: done || hasAllIntro(save) ? 1 : 0, target: 1 };
  if (!Number.isFinite(v.data)) return null;
  const current = counter(save, def, v);
  return current === null ? null : { current, target: v.data };
}

/** Whether the web can award the achievement at all. */
export function isSuccessAvailable(def: SuccessDef): boolean {
  return successProgress(PROBE, def) !== null;
}

// A probe account for `isSuccessAvailable`: only the shape matters.
const PROBE = {
  level: { level: 0 },
  money: 0,
  bet: { nb: 0 },
  miniboss: { classic: 0, golden: 0, diamond: 0 },
  success: { points: 0, unlocked: [] },
  stats: {
    money: { total: 0, boss: 0 },
    boss: { count: 0 },
    mission: { count: 0, fake: 0, loot: 0, xp: 0 },
    reward: { count: 0, inARow: 0 },
    dofus: { time: 0 },
    roll: { count1: 0, count2: 0 },
  },
} as unknown as BotUser;

function isEventDay(event: keyof typeof SUCCESS_EVENTS, now: number): boolean {
  const d = new Date(now);
  const e = SUCCESS_EVENTS[event];
  return d.getMonth() === e.month && d.getDate() === e.day;
}

/**
 * `updateSuccess`: points, currencies (fakecoins not counted in
 * `stats.money.total`, parity K-13, but in `totalMoneySuccess`), ornament and
 * title pushed as the bot does (a repeated title is pushed again until the
 * shared fix P-19 lands with T4.11), then the success-points cascade.
 */
function award(save: BotUser, def: SuccessDef, now: number, events: GameEvent[]): void {
  const r = def.rewards;
  save.success.points += r.points;
  save.success.unlocked.push(def.id);
  // Ids only: a text-named ornament (K-12) belongs to groups the web never checks.
  const ornament = typeof r.ornement === 'number' && r.ornement > 0 ? r.ornement : 0;
  if (ornament) save.ornements.owned.push(ornament);
  if (r.title) save.title.unlocked.push(r.title);
  save.money += r.fake;
  save.lootbox += r.loot;
  save.shopcoin += r.shop;
  save.legendaryCoin += r.leg;
  save.totalMoneySuccess += r.fake;
  save.totalLootSuccess += r.loot;
  save.totalShopSuccess += r.shop;
  events.push({
    type: 'successUnlocked',
    id: def.id,
    points: r.points,
    money: r.fake,
    lootbox: r.loot,
    shopcoin: r.shop,
    legendaryCoin: r.leg,
    title: r.title,
    ornament: ornaments[ornament] ? ornament : 0,
  });
  checkSuccessGroups(save, ['success'], now, events);
}

function isMet(save: BotUser, def: SuccessDef, now: number): boolean {
  const p = successProgress(save, def);
  if (!p) return false;
  if (p.event) return isEventDay(p.event, now);
  return p.current >= p.target;
}

/** Runs the bot's check of each group, in list order, awarding what is met. */
export function checkSuccessGroups(save: BotUser, groups: readonly SuccessGroup[], now: number, events: GameEvent[]): void {
  for (const group of groups) {
    for (const def of successDefs) {
      if (def.group !== group || save.success.unlocked.includes(def.id)) continue;
      if (isMet(save, def, now)) award(save, def, now, events);
    }
  }
}

/** Opening the profile (`user/profile.ts`): money and divers checks. */
export function viewProfile(save: BotUser, now: number, events: GameEvent[]): void {
  checkSuccessGroups(save, ['money', 'divers'], now, events);
}

/** `setTitle`: '' unequips; otherwise the title must be unlocked. */
export function equipTitle(save: BotUser, title: string, events: GameEvent[]): void {
  if (title !== '' && !save.title.unlocked.includes(title)) throw new GameError('titleLocked', { title });
  save.title.equip = title;
  events.push({ type: 'titleEquipped', title });
}

/** `equipOrnement`: 0 unequips; otherwise the ornament must be owned. */
export function equipOrnament(save: BotUser, id: number, events: GameEvent[]): void {
  const def = ornaments[id];
  if (!def || (id !== 0 && !save.ornements.owned.includes(id))) throw new GameError('ornamentLocked', { id });
  save.ornements.equip = { id, left: def.left, right: def.right };
  events.push({ type: 'ornamentEquipped', id });
}
